<script setup lang="ts">
import { ref, computed } from 'vue'
import { RouterLink } from 'vue-router'
import { authApi } from '@/api'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import AuthField from '@/components/auth/AuthField.vue'
import { useAnalytics } from '@/composables/useAnalytics'

const { capture } = useAnalytics()

const email = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const isEmailSent = ref(false)

const isFormValid = computed(() => email.value.includes('@'))

async function handleForgotPassword() {
  if (!isFormValid.value) return
  errorMessage.value = ''
  isLoading.value = true
  try {
    await authApi.authForgotPasswordPost({ email: email.value })
    capture('reset_requested')
    isEmailSent.value = true
  } catch (e: unknown) {
    console.error('Forgot password failed:', e)
    // Don't reveal whether the email exists or not for security
    capture('reset_requested')
    isEmailSent.value = true
  } finally {
    isLoading.value = false
  }
}

function resetForm() {
  capture('reset_resend_tapped')
  email.value = ''
  isEmailSent.value = false
  errorMessage.value = ''
}
</script>

<template>
  <div class="w-full max-w-md">
    <!-- Success state -->
    <template v-if="isEmailSent">
      <div class="text-center">
        <div class="w-16 h-16 bg-success-soft rounded-2xl flex items-center justify-center mx-auto mb-6">
          <font-awesome-icon :icon="['fas', 'envelope-circle-check']" class="text-3xl text-success-ink" />
        </div>
        <h1 class="text-2xl font-bold text-ink-900 mb-2">Check your email</h1>
        <p class="text-ink-500 mb-6">
          We've sent password reset instructions to<br />
          <span class="font-medium text-ink-900">{{ email }}</span>
        </p>

        <div class="bg-surface-variant rounded-xl p-4 mb-6 text-left">
          <div class="flex items-start gap-3">
            <font-awesome-icon :icon="['fas', 'circle-info']" class="text-[var(--primary)] mt-0.5" />
            <div class="text-sm text-ink-700">
              <p class="font-semibold mb-1">Didn't receive the email?</p>
              <ul class="list-disc list-inside space-y-1 text-ink-600">
                <li>Check your spam or junk folder</li>
                <li>Make sure you entered the correct email</li>
                <li>Wait a few minutes and try again</li>
              </ul>
            </div>
          </div>
        </div>

        <button class="text-sm font-medium text-[var(--primary)] hover:opacity-80" @click="resetForm">
          Try a different email
        </button>

        <div class="mt-8 pt-6 border-t border-ink-100">
          <RouterLink
            to="/login"
            class="flex items-center justify-center gap-2 text-ink-500 hover:text-ink-700 transition-colors"
          >
            <font-awesome-icon :icon="['fas', 'arrow-left']" />
            <span>Back to sign in</span>
          </RouterLink>
        </div>
      </div>
    </template>

    <!-- Form state -->
    <template v-else>
      <div class="text-center mb-7">
        <h1 class="text-2xl font-bold text-ink-900">Forgot password?</h1>
        <p class="text-ink-500 mt-1">No worries — enter your email and we'll send reset instructions.</p>
      </div>

      <div
        v-if="errorMessage"
        class="mb-5 p-4 bg-danger-soft border border-danger-ink/15 rounded-xl flex items-start gap-3"
      >
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-danger-ink mt-0.5" />
        <p class="text-sm text-danger-ink">{{ errorMessage }}</p>
      </div>

      <form class="space-y-4" @submit.prevent="handleForgotPassword">
        <AuthField
          v-model="email"
          label="EMAIL ADDRESS"
          :icon="['fas', 'envelope']"
          type="email"
          autocomplete="email"
          required
          placeholder="you@school.com"
        />
        <button
          type="submit"
          :disabled="!isFormValid || isLoading"
          class="w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 disabled:opacity-50 transition-colors"
        >
          {{ isLoading ? 'Sending…' : 'Send reset instructions' }}
        </button>
      </form>

      <div class="mt-7 text-center">
        <RouterLink
          to="/login"
          class="inline-flex items-center justify-center gap-2 text-sm text-ink-500 hover:text-ink-700 transition-colors"
        >
          <font-awesome-icon :icon="['fas', 'arrow-left']" />
          <span>Back to sign in</span>
        </RouterLink>
      </div>
    </template>
  </div>
</template>
